import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@glu/theming';
import classnames from 'classnames';
import { StarNextIcon, DocumentLinesIcon, HandshakeNextIcon } from '@glu/icons-react';

import RawHtml from 'common/RawHtml';
import ErrorMsg from 'common/ErrorMsg';
import renderGroups from 'utils/renderGroups';
import uiActions from 'core/ui/actions';
import rootStyles from './styles/root';

const styles = (theme) => ({
  button: {
    '&:hover, &:focus': {
      color: theme.links.link.color,
    },
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: theme.links.link.color,
    cursor: 'pointer',
    display: 'flex',
    fontFamily: theme.actions.callToAction.fontFamily,
    fontSize: theme.actions.callToAction.fontSize,
    margin: 0,
    padding: 0,
    textAlign: 'left',
  },
  buttonLabel: {
    [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
      display: 'none',
    },
  },
  buttonMobileLabel: {
    [`@media(min-width: ${theme.screenSizes.mobileMax}px)`]: {
      display: 'none',
    },
  },
  buttonWrap: {
    '&:last-child': {
      border: 0,
    },
    borderBottom: `1px solid ${theme.palette.form.border}`,
    display: 'block',
    padding: [theme.spacing.sm, 0],
  },
  icon: {
    '& svg': {
      fill: theme.palette.icon,
      height: 32,
      width: 32,
    },
    marginRight: theme.spacing.xs,
  },
  navGroup: {
    display: 'block',
    margin: [0, 'auto'],
    maxWidth: 440,
    [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
      maxWidth: 272,
    },
  },
  root: {
    ...rootStyles(theme),
    '& fieldset[class*="-group-view"]:not(.active)': {
      display: 'none',
    },
  },
});

export const Launch = ({ activeGroup, classes, groups, headerHtml, logoutUrl, setActiveGroup }) => {
  const getIcon = useCallback(
    (groupType) => {
      const iconProps = { className: classes.icon };
      switch (groupType) {
        case 'RESUME_APPLICATION':
          return <DocumentLinesIcon {...iconProps} />;
        case 'EXISTING_CUSTOMER':
          return <StarNextIcon {...iconProps} />;
        case 'NEW_CUSTOMER':
          return <HandshakeNextIcon {...iconProps} />;
        default:
          return null;
      }
    },
    [classes.icon],
  );
  return (
    <div className={classnames('launch-view', classes.root)}>
      <RawHtml className="header-html">{headerHtml}</RawHtml>

      <ErrorMsg type="page" />

      {!activeGroup ? (
        <div className={classnames('row', classes.navGroup)}>
          <div className="widget">
            {Object.keys(groups).map((gKey) => (
              <div key={gKey} className={classes.buttonWrap}>
                <button
                  type="button"
                  variant="secondary"
                  className={classes.button}
                  onClick={() => {
                    if (groups[gKey].staticGroupType === 'RESUME_APPLICATION') {
                      window.location = logoutUrl;
                    } else {
                      setActiveGroup(gKey);
                    }
                  }}
                >
                  {getIcon(groups[gKey].staticGroupType)}
                  <span className={classes.buttonMobileLabel}>{groups[gKey].label}</span>
                  <span className={classes.buttonLabel}>{groups[gKey].labelMobile}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="widget">
            <div className="region-groups-nav">{renderGroups(groups)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

Launch.propTypes = {
  activeGroup: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  groups: PropTypes.shape({
    label: PropTypes.string,
    labelMobile: PropTypes.string,
  }).isRequired,
  headerHtml: PropTypes.string,
  logoutUrl: PropTypes.string.isRequired,
  setActiveGroup: PropTypes.func.isRequired,
};

Launch.defaultProps = {
  activeGroup: '',
  headerHtml: '',
};

const mapStateToProps = ({ groups, page, timeOut, ui }) => ({
  activeGroup: ui.activeGroup,
  groups,
  headerHtml: page.headerHtml,
  logoutUrl: timeOut.logoutUrl,
});

const LaunchStyled = withStyles(styles)(Launch);

export default connect(mapStateToProps, { setActiveGroup: uiActions.setActiveGroup })(LaunchStyled);
